import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import RadioGroup from '@mui/material/RadioGroup';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { FormControlLabel, Radio } from './styled';
import IconLargeCicle from '../../shared/IconLargeCicle';
import { useCancelScheduleReasonByIdMutation } from '../../../services/schedule.service';
import ExpiredConfirmationSchedule from '../ExpiredConfirmationSchedule';

interface CancelReason {
  reason: string;
  reason_other: string;
}

export default function CancelReasonFeedbackRoot() {
  const {
    register,
    handleSubmit,
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors }
  } = useForm<CancelReason>();
  const [success, setSuccess] = useState(false);
  const [isExpire, seIsExpire] = useState(false);
  const { scheduleId } = useParams();
  const { state } = useLocation();
  const methods = useForm();
  const [putCancelScheduleReasonById, { isLoading }] = useCancelScheduleReasonByIdMutation();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('reason', event.target.value);
    setValue('reason_other', '');
    trigger('reason');
  };

  const onSubmit = (data: CancelReason) => {
    const payload = {
      reason: data.reason_other || data.reason
    };
    if (!state.token) {
      seIsExpire(true);
      return;
    }
    return putCancelScheduleReasonById({ id: scheduleId || '', reason: payload.reason, token: state.token })
      .unwrap()
      .then(() => {
        setSuccess(true);
      });
  };

  if (isLoading) {
    return <CircularProgress sx={{ color: 'brand.darkGreen' }} />;
  }

  if (isExpire) {
    return <ExpiredConfirmationSchedule />;
  }

  if (success) {
    return (
      <>
        <IconLargeCicle>
          <SentimentSatisfiedAltIcon />
        </IconLargeCicle>
        <Typography variant="h2" sx={{ mb: 3 }}>
          Obrigada por nos avisar!
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Seu feedback foi registrado com sucesso, agradecemos por nos ajudar a melhorar.
        </Typography>
      </>
    );
  }

  return (
    <>
      <IconLargeCicle>
        <CheckIcon />
      </IconLargeCicle>

      <Typography variant="h2" sx={{ mb: 3 }}>
        Seu agendamento foi cancelado.
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Obrigada por nos avisar! Nos vemos quando for o melhor momento para você. Conte com a Oya para te receber com todo o
        acolhimento e cuidado que valorizamos.
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        Pode nos contar o motivo do cancelamento?
      </Typography>

      <FormProvider {...methods}>
        <form onSubmit={(e: any) => e.preventDefault()}>
          <Controller
            name="reason"
            control={control}
            rules={{
              required: 'Campo obrigatório'
            }}
            render={({ field }) => (
              <FormControl error={!!errors?.reason} fullWidth>
                <RadioGroup
                  {...field}
                  name="reason"
                  value={field.value || ''}
                  onChange={e => {
                    field.onChange(e);
                    handleChange(e);
                  }}
                >
                  <FormControlLabel value="Tenho outro compromisso" control={<Radio />} label="Tenho outro compromisso" />
                  <FormControlLabel
                    value="Não preciso mais realizar a consulta"
                    control={<Radio />}
                    label="Não preciso mais realizar a consulta"
                  />
                  <FormControlLabel value="Prefiro não informar" control={<Radio />} label="Prefiro não informar" />
                  <FormControlLabel value="outro" control={<Radio />} label="Outro" />
                </RadioGroup>
              </FormControl>
            )}
          />

          {getValues('reason') === 'outro' && (
            <Controller
              name="reason_other"
              control={control}
              rules={{
                required: 'Campo obrigatório'
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="reason_other"
                  type="text"
                  variant="standard"
                  value={getValues('reason_other')}
                  error={!!errors?.reason_other}
                  helperText={errors?.reason_other?.message}
                  inputProps={{
                    classes: {
                      input: 'multilineColor'
                    }
                  }}
                  fullWidth
                  {...register('reason_other', {
                    required: 'Campo Obrigatório',
                    onChange: () => {
                      trigger('reason_other');
                    }
                  })}
                />
              )}
            />
          )}

          {errors?.reason?.message && <FormHelperText error>{errors?.reason?.message}</FormHelperText>}

          <Button sx={{ mt: 5 }} variant="contained" onClick={handleSubmit(onSubmit)}>
            Enviar feeback
          </Button>
        </form>
      </FormProvider>
    </>
  );
}
